import { Brain, FileText, TreeStructure, Plus, Trash } from "@phosphor-icons/react";
import type {
  FeaturePalette,
  Result,
  ScopeNode,
  SearchProvider,
  Verb,
} from "@/shared/components/command-palette/types";
import { getNotes } from "./api/notes";
import { getCategories } from "./api/categories";

const NOTES_PARENT: ScopeNode = {
  id: "notes",
  parentId: null,
  title: "Notes",
};

const CATEGORIES_PARENT: ScopeNode = {
  id: "notes.categories",
  parentId: "notes",
  title: "Categories",
};

const scopes: ScopeNode[] = [
  {
    id: "notes.new",
    parentId: "notes",
    title: "New note",
    icon: Plus,
    href: "/notes/new",
  },
  {
    id: "notes.categories",
    parentId: "notes",
    title: "Categories",
    icon: TreeStructure,
    href: "/notes/categories",
    resolveChildren: async () => {
      try {
        const res = await getCategories({ limit: 100 });
        return res.items.map((c) => ({
          id: `notes.categories.${c.id}`,
          parentId: "notes.categories",
          title: c.title,
          icon: TreeStructure,
          href: `/notes/categories/${c.id}`,
        }));
      } catch {
        return [];
      }
    },
  },
];

const noteVerbs: Verb[] = [
  {
    id: "notes.open",
    title: "Open note",
    icon: FileText,
    run: (entity, ctx) => {
      const id = (entity as { id: string }).id;
      ctx.router.push(`/notes/${id}`);
    },
  },
  {
    id: "notes.edit",
    title: "Edit note",
    icon: FileText,
    run: (entity, ctx) => {
      const id = (entity as { id: string }).id;
      ctx.router.push(`/notes/${id}/edit`);
    },
  },
  {
    id: "notes.versions",
    title: "Show versions",
    icon: FileText,
    run: (entity, ctx) => {
      const id = (entity as { id: string }).id;
      ctx.router.push(`/notes/${id}/versions`);
    },
  },
  {
    id: "notes.add-srs",
    title: "Add to SRS",
    icon: Brain,
    run: () => {
      // Wired through SRS feature; intentional no-op placeholder until
      // the create-card-from-note flow lands in the palette context.
    },
  },
  {
    id: "notes.delete",
    title: "Delete note",
    icon: Trash,
    destructive: true,
    confirm: {
      title: "Delete this note?",
      body: "The note will be removed permanently.",
      cta: "Delete",
    },
    run: () => {
      // Intentional placeholder — mutating delete is gated on the
      // confirm page and goes through the feature's mutation hook
      // once invoked from a page that owns react-query context.
    },
  },
];

const notesProvider: SearchProvider = {
  id: "notes",
  label: "Notes",
  icon: FileText,
  prefix: "@notes",
  async search(q, signal) {
    if (!q) return [];
    const res = await getNotes({ search: q, limit: 6, semantic: true });
    if (signal.aborted) return [];
    return res.items.map<Result>((n) => {
      const title = n.title || "Untitled";
      const scopeChain = [NOTES_PARENT];
      const onSelect = (cx: import("@/shared/components/command-palette/types").PaletteCtx) =>
        cx.push({
          kind: "entity",
          result: {
            id: `note:${n.id}`,
            title,
            icon: FileText,
            scopeChain,
            onSelect: () => {},
            verbs: noteVerbs,
            entity: n,
          },
        });
      return {
        id: `note:${n.id}`,
        title,
        subtitle: n.note_kind ?? undefined,
        icon: FileText,
        scopeChain,
        score: 55,
        onSelect,
        verbs: noteVerbs,
        entity: n,
      };
    });
  },
};

const categoriesProvider: SearchProvider = {
  id: "categories",
  label: "Categories",
  icon: TreeStructure,
  prefix: "@categories",
  async search(q, signal) {
    if (!q) return [];
    const res = await getCategories({ search: q, limit: 6 });
    if (signal.aborted) return [];
    return res.items.map<Result>((c) => ({
      id: `cat:${c.id}`,
      title: c.title,
      subtitle: c.slug ? `/${c.slug}` : undefined,
      icon: TreeStructure,
      scopeChain: [NOTES_PARENT, CATEGORIES_PARENT],
      score: 45,
      onSelect: (ctx) => {
        ctx.router.push(`/notes/categories/${c.id}`);
        ctx.close();
      },
    }));
  },
};

export const notesPalette: FeaturePalette = {
  scopes,
  commands: [
    {
      id: "notes.create",
      title: "Create new note",
      icon: Plus,
      keywords: ["new", "note", "create"],
      scopeId: "notes",
      run: (ctx) => ctx.router.push("/notes/new"),
    },
  ],
  searchProviders: [notesProvider, categoriesProvider],
};
